import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { ProductOnUser, Collection, StoreCategory } from 'common-ts/dist/models/Product';
import { ProductsList } from 'common-ts/dist/models/Product';
import axios from 'axios';
import { RootState } from '../store';
import { Mixpanel } from '../mixpanel';
import { BodyMeasurementsForSize } from 'common-ts/dist/models/UserMeasurements';
import { RegularSizeValue } from 'common-ts/dist/models/Cart';
import { setProductSpotlightList } from './CartRedux';
import { buildAllProductsCollection, buildSizeAvailabilityDict, fetchCatalog } from '../terbebasApi';

// Define the initial state using that type
const initialState: ProductsList = {
  collectionsDictionary: {},
  productTemplatesById: {},
  defaultProductTemplateId: undefined,
  categoriesDictionary: {},
  categoriesOrder: [],
  productsDictionary: {},

  discoveryCollectionsOrder: [],
  efficientCollectionsOrder: [],
  efficientProductsOrder: [],
  discoveryCollectionsOrderDict: {},

  productViewEventTimerDict: {},

  productFamilyMediaHasViewedDict: {},

  status: APIRequestStatus.Idle,
  error: null,

  productSizeAvailabilityDict: {},

  statusSizeAvailability: APIRequestStatus.Idle,
}

const incrementPricePercent = 4;
const getPriceAfterAdjustment = (beforePrice: number, productId: string) => {

  // Disable this per March 30
  return beforePrice;

  // IF THE PRODUCT IS NEW, WE MAINTAIN LIMIT PRICE
  if (productId?.substring(0, 7) >= '2025-10') {
    return beforePrice;
  }
  // MAINTAIN NULL PRICING
  if (beforePrice === -1) {
    return -1;
  }

  const draftIncreasePrice = beforePrice * incrementPricePercent / 100;
  const roundedDraftIncreasePrice = Math.ceil(draftIncreasePrice / 500) * 500; // Rounda to nearest ceiling 500

  let afterPrice = beforePrice + roundedDraftIncreasePrice;

  // MAINTAIN PRICE BRACKETING
  if (beforePrice < 300000 && afterPrice >= 300000) {
    afterPrice = beforePrice > 298500 ? beforePrice : 298500;
  } else if (beforePrice < 200000 && afterPrice >= 200000) {
    afterPrice = beforePrice > 198000 ? beforePrice : 198000;
  }

  return afterPrice;
};

// productUpdatePost endpoint (also hosts the offline-branch price override read).
const PRODUCT_UPDATE_POST_URL =
  'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/product/update';

type BranchPriceOverride = {
  originalPrice?: number;
  discountedPrice?: number;
  extraSizeChargePrice?: number;
};

// Last-fetched offline-branch overrides, kept module-side so a products refresh can re-apply
// them without widening the shared ProductsList type.
let cachedBranchOverrides: Record<string, BranchPriceOverride> = {};

// The offline store device is pinned to a branch via localStorage (set once). Only honor it
// in the offline-store context — an internal iframe or the /offline-measurements route — so a
// regular customer's phone/browser never picks up branch pricing (uses the base dictionary).
export const getOfflineBranchId = (): string | null => {
  try {
    const isInsideIframe = window.self !== window.top;
    const isOfflineRoute = window.location.href.includes('offline-measurements');
    if (!isInsideIframe && !isOfflineRoute) return null;
    return localStorage.getItem('offlineBranchId');
  } catch {
    return null;
  }
};

// Overlay branch price overrides onto the loaded products (mutates Immer draft in place).
function applyBranchOverridesToDict(
  productsDictionary: any,
  overrides: Record<string, BranchPriceOverride>
) {
  for (const productId of Object.keys(overrides || {})) {
    const product = productsDictionary[productId];
    if (!product) continue;
    const ov = overrides[productId];
    if (ov.originalPrice !== undefined) product.originalPrice = ov.originalPrice;
    if (ov.discountedPrice !== undefined) product.discountedPrice = ov.discountedPrice;
    if (ov.extraSizeChargePrice !== undefined)
      product.extraSizeChargePrice = ov.extraSizeChargePrice;
  }
}

export const fetchBranchPriceOverrides = createAsyncThunk(
  'productsList/fetchBranchPriceOverrides',
  async () => {
    const branchId = getOfflineBranchId();
    if (!branchId) return {} as Record<string, BranchPriceOverride>;
    const response = await axios({
      method: 'post',
      url: PRODUCT_UPDATE_POST_URL,
      data: JSON.stringify({ action: 'getBranchPriceOverrides', branchId })
    });
    return (response.data?.overrides ?? {}) as Record<string, BranchPriceOverride>;
  }
);

// Fetches the catalog from terbebas-backend and reshapes it into the
// payload the legacy reducer expects. Stock availability is derived
// locally from variant inventory (no separate availability service).
export const fetchProductsList = createAsyncThunk('productsList/fetchProductsList', async (payload: any, thunkAPI) => {
  const { products, collections, collectionOrder, curatedHandles, categories, activeDiscounts,
    productTemplates } = await fetchCatalog();

  // '__all__' is a synthetic collection whose discovery block renders the
  // whole catalog as a grid. The /shop discovery view shows the collections
  // the merchant curated layouts for; with none curated, the plain grid.
  const allCollection = buildAllProductsCollection(products);
  const discoveryCollectionsOrder = curatedHandles.length
    ? curatedHandles
    : [allCollection.collectionId];

  return {
    products,
    collections: [...collections, allCollection],
    categories,
    discoveryCollectionsOrder,
    discoveryCollectionsOrderDict: {},
    efficientCollectionsOrder: collectionOrder,
    efficientProductsOrder: products.map((p) => p.productId),
    sizeAvailabilityDict: buildSizeAvailabilityDict(products),
    activeDiscounts,
    productTemplates,
  };
})

export const fetchProductsSizeAvailabilityDict = createAsyncThunk('productsList/fetchProductsSizeAvailabilityDict', async (payload: string[], thunkAPI) => {

  var measurementsArray = [];
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XXS]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XS]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.S]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.M]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.L]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XL]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XXL]);

  const response = await axios({
    method: 'post',
    url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/availability/product-size',
    data: JSON.stringify({
      productIdArray: payload,
      measurementsArray: measurementsArray
    })
  });

  if (response.data.productFlashSaleList) {
    thunkAPI.dispatch(setProductSpotlightList(response.data.productFlashSaleList));
  }

  return JSON.parse(response.data.sizeAvailability);
})

// export const attemptProductGlimpseEvent = createAsyncThunk('productsList/attemptProductGlimpseEvent', 
// async (payload: string, thunkAPI) => {
//   let productViewEventTimerDict = (thunkAPI.getState() as RootState).productsList.productViewEventTimerDict;

//   var currentTimestamp = Math.floor(Date.now() / 1000); // Timestamp in seconds

//   // We send another event if more than 10 seconds have passed since the last glimpse event
//   if (currentTimestamp - productViewEventTimerDict[payload] > 10) {
//     productViewEventTimerDict[payload] = currentTimestamp;
    
//   }

//   return response.data
// })

export const productSlice = createSlice({
  name: 'productsList',
  initialState,
  reducers: {
    attemptProductGlimpseEvent: (state, action: PayloadAction<string>) => {
      var currentTimestamp = Math.floor(Date.now() / 1000); // Timestamp in seconds

      // We send another event if more than 10 seconds have passed since the last glimpse event.
      // Each "impression" should have a 10 second interval from one another,
      // and is counted as one view / impression.
      // whereas one "reach" follows the one reach per user rule.
      if (state.productViewEventTimerDict[action.payload] === undefined
        || currentTimestamp - state.productViewEventTimerDict[action.payload] > 10) {
        state.productViewEventTimerDict[action.payload] = currentTimestamp;

        Mixpanel.track('productGlimpse', {
          'productId': "productId-" + action.payload,
          'timestamp': currentTimestamp
        });
        console.log("Tracking mixpanel for productId " + action.payload + " and timestamp " + currentTimestamp);
      }
    },
    updateProductFamilyMediaHasViewedDict: (state, action: PayloadAction<string[]>) => {
      for (let familyMediaUrl of action.payload) {
        state.productFamilyMediaHasViewedDict[familyMediaUrl] = true;
      }
    },
    // Dashboard layout preview only: registers the synthetic collection built
    // from the draft's product selection so BlockCollectionProducts can
    // resolve it like any other Koleksi.
    setPreviewCollection: (state, action: PayloadAction<Collection>) => {
      state.collectionsDictionary[action.payload.collectionId] = action.payload;
    },
    // Dashboard-supplied product for the layout preview. The public catalog
    // is ACTIVE-only, so a DRAFT product simply isn't in it — and an unsaved
    // edit isn't in it either. The dashboard already holds the full product,
    // so it pushes it in rather than the storefront fetching it, which keeps
    // the ACTIVE-only rule intact (nothing is exposed to shoppers).
    setPreviewProduct: (state, action: PayloadAction<ProductOnUser>) => {
      state.productsDictionary[action.payload.productId] = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchProductsList.pending, (state, action) => {
        state.status = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchProductsList.fulfilled, (state, action) => {
        // Products arrive pre-mapped from terbebasApi (ProductOnUser shape).
        state.productsDictionary = {};
        for (const productDetail of action.payload.products as ProductOnUser[]) {
          state.productsDictionary[productDetail.productId] = productDetail;
        }

        state.collectionsDictionary = {};
        for (const collectionDetail of action.payload.collections as Collection[]) {
          state.collectionsDictionary[collectionDetail.collectionId] = collectionDetail;
        }

        state.categoriesDictionary = {};
        state.categoriesOrder = [];
        for (const categoryDetail of (action.payload.categories ?? []) as StoreCategory[]) {
          state.categoriesDictionary[categoryDetail.categoryId] = categoryDetail;
          state.categoriesOrder.push(categoryDetail.categoryId);
        }

        // Product-detail layouts, indexed for O(1) resolution per product.
        state.productTemplatesById = {};
        state.defaultProductTemplateId = undefined;
        for (const template of (action.payload.productTemplates ?? [])) {
          state.productTemplatesById[template.id] = template;
          if (template.isDefault) state.defaultProductTemplateId = template.id;
        }

        state.discoveryCollectionsOrder = action.payload.discoveryCollectionsOrder;
        state.discoveryCollectionsOrderDict = action.payload.discoveryCollectionsOrderDict ?? {};
        state.efficientCollectionsOrder = action.payload.efficientCollectionsOrder;
        state.efficientProductsOrder = action.payload.efficientProductsOrder;

        // Stock is derived from variant inventory, not a separate service.
        state.productSizeAvailabilityDict = action.payload.sizeAvailabilityDict;
        state.activeDiscounts = action.payload.activeDiscounts ?? [];
        state.statusSizeAvailability = APIRequestStatus.Success;

        state.status = APIRequestStatus.Success;
      })
      .addCase(fetchProductsList.rejected, (state, action) => {
        state.status = APIRequestStatus.Failure;
        // state.error = action.error.message
        console.log("fetch is failure");
      })
      .addCase(fetchBranchPriceOverrides.fulfilled, (state, action) => {
        cachedBranchOverrides = action.payload;
        applyBranchOverridesToDict(state.productsDictionary, cachedBranchOverrides);
      })
      .addCase(fetchProductsSizeAvailabilityDict.pending, (state, action) => {
        state.statusSizeAvailability = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchProductsSizeAvailabilityDict.fulfilled, (state, action) => {
        // Payload is already in Object format.

        // Product Size Availability Dict
        state.productSizeAvailabilityDict = action.payload;

        state.statusSizeAvailability = APIRequestStatus.Success;
      })
      .addCase(fetchProductsSizeAvailabilityDict.rejected, (state, action) => {
        state.statusSizeAvailability = APIRequestStatus.Failure;
      })
  }
})

// Action creators are generated for each case reducer function
export const { attemptProductGlimpseEvent, updateProductFamilyMediaHasViewedDict, setPreviewCollection, setPreviewProduct } = productSlice.actions

export default productSlice.reducer